import React from 'react';
import { t } from '../../utils/translations';
import { getFormattedUserName } from '../../utils/cvUtils';

export default function CVTemplateATS({ user, projects, language = 'id' }) {
  const fallbackAvatar = 'https://ui-avatars.com/api/?name=User&background=059669&color=fff';

  // Helper function to categorize education
  const categorizeEducation = (education) => {
    const higher = [];
    const k12 = [];
    
    education.forEach(edu => {
      const degree = edu.degree?.toLowerCase() || '';
      const isHigherEd = degree.includes('sarjana') || degree.includes('s1') || 
                        degree.includes('s2') || degree.includes('s3') || 
                        degree.includes('bachelor') || degree.includes('master') || 
                        degree.includes('magister') || degree.includes('phd') || 
                        degree.includes('doktor') || degree.includes('diploma');
      
      if (isHigherEd) {
        higher.push(edu);
      } else {
        k12.push(edu);
      }
    });
    
    return { higher, k12 };
  };

  const { higher: higherEducation, k12: k12Education } = user?.education ? 
    categorizeEducation(user.education) : { higher: [], k12: [] };

  return (
    <div className="cv-template avoid-break bg-white p-4 sm:p-6 md:p-8 max-w-[21cm] mx-auto print:p-8 print:shadow-none print:max-w-full" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Photo at Top Center - ATS Friendly */}
      <div className="cv-section text-center mb-4 sm:mb-6 avoid-break">
        <div className="cv-photo w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full border-4 border-gray-300 overflow-hidden mx-auto mb-3 sm:mb-4 bg-gray-100 print:w-32 print:h-32">
          {user?.photo ? (
            <img src={user.photo} alt={user.full_name} className="w-full h-full object-cover" />
          ) : (
            <img src={fallbackAvatar} alt="Avatar" className="w-full h-full object-cover" />
          )}
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">{getFormattedUserName(user) || 'YOUR NAME'}</h1>
        <p className="text-base sm:text-lg text-gray-600 mb-2 sm:mb-3 text-center">{user?.job || 'Professional Title'}</p>
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
          {user?.email && <span>{user.email}</span>}
          {user?.phone && <span>|</span>}
          {user?.phone && <span>{user.phone}</span>}
          {user?.location && <span>|</span>}
          {user?.location && <span>{user.location}</span>}
        </div>
      </div>

      <hr className="border-t-2 border-gray-300 mb-4 sm:mb-6" />

      {/* Professional Summary */}
      {user?.summary && (
        <div className="cv-section mb-4 sm:mb-6 avoid-break">
          <h2 className="cv-section-title text-base sm:text-lg font-bold text-gray-900 uppercase tracking-wide mb-2 sm:mb-3 border-b-2 border-gray-900 pb-1">
            {t('professionalSummary', language)}
          </h2>
          <p className="text-xs sm:text-sm text-gray-800 leading-relaxed text-justify">{user.summary}</p>
        </div>
      )}

      {/* Core Competencies */}
      {user?.skills && user.skills.length > 0 && (
        <div className="cv-section mb-4 sm:mb-6 avoid-break">
          <h2 className="cv-section-title text-base sm:text-lg font-bold text-gray-900 uppercase tracking-wide mb-2 sm:mb-3 border-b-2 border-gray-900 pb-1">
            {t('coreCompetencies', language)}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {user.skills.map((skill, i) => (
              <div key={i} className="text-xs sm:text-sm text-gray-800">• {skill}</div>
            ))}
          </div>
        </div>
      )}

      {/* Professional Experience */}
      {user?.experience && user.experience.length > 0 && (
        <div className="cv-section mb-4 sm:mb-6 avoid-break">
          <h2 className="cv-section-title text-base sm:text-lg font-bold text-gray-900 uppercase tracking-wide mb-2 sm:mb-3 border-b-2 border-gray-900 pb-1">
            {t('professionalExperience', language)}
          </h2>
          <div className="space-y-3 sm:space-y-4">
            {user.experience.map((exp, i) => (
              <div key={i} className="cv-experience-item avoid-break">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline mb-1">
                  <h3 className="text-sm sm:text-base font-bold text-gray-900">{exp.position}</h3>
                  <span className="text-xs sm:text-sm text-gray-600">{exp.period}</span>
                </div>
                <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">{exp.company}</p>
                {exp.description && (
                  <p className="text-xs sm:text-sm text-gray-800 leading-relaxed text-justify">• {exp.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Education - Higher Education */}
      {higherEducation.length > 0 && (
        <div className="cv-section mb-4 sm:mb-6 avoid-break">
          <h2 className="cv-section-title text-base sm:text-lg font-bold text-gray-900 uppercase tracking-wide mb-2 sm:mb-3 border-b-2 border-gray-900 pb-1">
            {t('higherEducation', language)}
          </h2>
          <div className="space-y-2 sm:space-y-3">
            {higherEducation.map((edu, i) => (
              <div key={i} className="cv-education-item avoid-break">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline">
                  <h3 className="text-sm sm:text-base font-bold text-gray-900">{edu.degree}</h3>
                  <span className="text-xs sm:text-sm text-gray-600">{edu.year}</span>
                </div>
                <p className="text-xs sm:text-sm text-gray-700">{edu.school || edu.institution}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Education - K-12 */}
      {k12Education.length > 0 && (
        <div className="cv-section mb-4 sm:mb-6 avoid-break">
          <h2 className="cv-section-title text-base sm:text-lg font-bold text-gray-900 uppercase tracking-wide mb-2 sm:mb-3 border-b-2 border-gray-900 pb-1">
            {t('formalEducation', language)}
          </h2>
          <div className="space-y-2 sm:space-y-3">
            {k12Education.map((edu, i) => (
              <div key={i} className="cv-education-item avoid-break">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline">
                  <h3 className="text-sm sm:text-base font-bold text-gray-900">{edu.degree}</h3>
                  <span className="text-xs sm:text-sm text-gray-600">{edu.year}</span>
                </div>
                <p className="text-xs sm:text-sm text-gray-700">{edu.school || edu.institution}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Projects */}
      {projects && projects.length > 0 && (
        <div className="cv-section mb-4 sm:mb-6 avoid-break">
          <h2 className="cv-section-title text-base sm:text-lg font-bold text-gray-900 uppercase tracking-wide mb-2 sm:mb-3 border-b-2 border-gray-900 pb-1">
            {t('projects', language)}
          </h2>
          <div className="space-y-2 sm:space-y-3">
            {projects.map((proj, i) => (
              <div key={i} className="cv-project-item avoid-break">
                <h3 className="text-xs sm:text-sm font-bold text-gray-900">{proj.title}</h3>
                <p className="text-xs sm:text-sm text-gray-800 text-justify">• {proj.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
